import React, { useState, useEffect, useRef } from 'react'
import api from '../../lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import {
  Sparkles,
  Globe,
  Check,
  X,
  Loader2,
  Link2,
  Clock,
  Building,
  AlertTriangle,
  Phone,
  Mail,
  MapPin,
  Pencil,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { motion, AnimatePresence } from 'framer-motion'
import { US_TIMEZONES, detectBrowserTimezone } from '@/lib/timezones'

type Phase = 'input' | 'scanning' | 'results' | 'manual-fallback'

interface ScrapedDataResponse {
  success: boolean
  status: 'complete' | 'pending' | 'not_started'
  data?: {
    practiceAreas: string[]
    businessName: string
    description: string
    contactInfo: {
      phone?: string
      email?: string
      address?: string
    }
    homepageScreenshot?: string | null
  }
}

// Canonical practice-area list. MUST stay in sync with the canonical list
// embedded in the GPT prompt at packages/api/src/services/WebScraperService.ts
// (cleanSummaryWithGPT) — GPT rolls sub-specialties up to these umbrellas, so
// drift will silently drop scraped areas at the frontend match step.
const PRACTICE_AREAS = [
  'Personal Injury',
  'Car Accidents',
  'Medical Malpractice',
  'Workers Compensation',
  'Criminal Defense',
  'Family Law',
  'Divorce',
  'Immigration',
  'Employment Law',
  'Real Estate',
  'Estate Planning',
  'Bankruptcy',
  'Business Law',
  'Intellectual Property',
  'Construction Law',
  'Tax Law',
  'Civil Litigation',
  'Elder Law',
  'General Practice',
]

const SCANNING_MESSAGES = [
  'Knocking on your website\u2019s front door...',
  'Poking around your homepage...',
  'Reading up on your firm...',
  'Cooking up your knowledge base...',
  'Meeting your team...',
  'Sorting through your practice areas...',
  'Discombobulating the contact page...',
  'Teaching your AI your firm\u2019s voice...',
  'Polishing the final details...',
]

interface OrganizationSetupStepProps {
  onComplete: (data: {
    id: string
    name: string
    slug: string
    website?: string
    practiceAreas?: string[]
    description?: string
    organizationName?: string
  }) => void
  onBack?: () => void
  initialData?: {
    id?: string
    name?: string
    slug?: string
    website?: string
    practiceAreas?: string[]
    description?: string
    organizationName?: string
  }
  // Verified email from step 1 — prefilled into the Contact Info email field
  // on the results screen so the firm email defaults to the account owner.
  signupEmail?: string
}

export default function OrganizationSetupStep({
  onComplete,
  onBack,
  initialData,
  signupEmail,
}: OrganizationSetupStepProps) {
  // If initialData has an org id and practice areas, skip straight to results
  const initialPhase: Phase =
    initialData?.id && initialData?.name ? 'results' : 'input'

  const [phase, setPhase] = useState<Phase>(initialPhase)
  const [websiteUrl, setWebsiteUrl] = useState(initialData?.website || '')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [orgId, setOrgId] = useState(initialData?.id || '')
  const { toast } = useToast()

  // Scanning state
  const [scanMessageIndex, setScanMessageIndex] = useState(0)
  const pollRef = useRef<NodeJS.Timeout | null>(null)
  const scanMsgRef = useRef<NodeJS.Timeout | null>(null)

  // Results state
  const [firmName, setFirmName] = useState(initialData?.name || initialData?.organizationName || '')
  const [slug, setSlug] = useState(initialData?.slug || '')
  const [editingSlug, setEditingSlug] = useState(false)
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null)
  const [checkingSlug, setCheckingSlug] = useState(false)
  const [practiceAreas, setPracticeAreas] = useState<string[]>(initialData?.practiceAreas || [])
  const [description, setDescription] = useState(initialData?.description || '')
  const [contactInfo, setContactInfo] = useState<{ phone?: string; email?: string; address?: string }>(
    signupEmail ? { email: signupEmail } : {}
  )
  const [screenshot, setScreenshot] = useState<string | null>(null)
  // 'loading' while the img decodes, 'ok' if it passes the blank-check,
  // 'blank' if the capture is mostly white (Firecrawl serves a valid PNG even
  // when the hero hasn't rendered), 'failed' if the URL 404s or CORS blocks.
  const [screenshotStatus, setScreenshotStatus] = useState<'loading' | 'ok' | 'blank' | 'failed'>('loading')
  const [isConfirming, setIsConfirming] = useState(false)

  // Manual fallback state
  const [manualTimezone, setManualTimezone] = useState(detectBrowserTimezone())

  // Reset screenshot status whenever we get a new URL/data-URI to evaluate
  useEffect(() => {
    setScreenshotStatus(screenshot ? 'loading' : 'failed')
  }, [screenshot])

  // Sample the loaded image. Firecrawl will happily return an all-white PNG
  // when the hero didn't finish rendering — we want to show a fallback instead
  // of a giant white rectangle. Threshold: >88% near-white pixels = blank.
  const checkImageBlank = (imgEl: HTMLImageElement) => {
    try {
      const canvas = document.createElement('canvas')
      // Sample at a small resolution — full-res reads are expensive and
      // unnecessary for a heuristic.
      const w = canvas.width = 120
      const h = canvas.height = 75
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        setScreenshotStatus('ok')
        return
      }
      ctx.drawImage(imgEl, 0, 0, w, h)
      const pixels = ctx.getImageData(0, 0, w, h).data
      let whitish = 0
      let total = 0
      for (let i = 0; i < pixels.length; i += 4) {
        const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2]
        if (r > 240 && g > 240 && b > 240) whitish++
        total++
      }
      const ratio = whitish / total
      setScreenshotStatus(ratio > 0.88 ? 'blank' : 'ok')
    } catch {
      // Cross-origin canvas read — trust the image and show it
      setScreenshotStatus('ok')
    }
  }

  // Resuming onboarding mid-step-2: if we already have an org id (the "Analyze
  // my website" POST created it) but we haven't cached any scraped data in
  // component state, fetch it from the backend. If the scrape is still running,
  // transition to the scanning animation and poll.
  useEffect(() => {
    if (initialPhase !== 'results' || !orgId) return
    const fieldsEmpty = !description && practiceAreas.length === 0 && !contactInfo.email && !contactInfo.phone
    if (!fieldsEmpty) return
    ;(async () => {
      try {
        const res = await api.get<ScrapedDataResponse>('/api/onboarding/scraped-data')
        if (res.data.success && res.data.status === 'complete' && res.data.data) {
          handleScrapeComplete(res.data.data)
        } else {
          setPhase('scanning')
          startMessageCycle()
          startPolling()
        }
      } catch {
        // If scraped-data fetch fails outright, leave user on results phase
        // with whatever's already filled — they can edit before confirming.
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Auto-generate slug from firm name
  useEffect(() => {
    if (firmName && !editingSlug) {
      const generated = firmName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 50)
      setSlug(generated)
    }
  }, [firmName, editingSlug])

  // Check slug availability with debounce (only in results/manual phase)
  useEffect(() => {
    if (phase !== 'results' && phase !== 'manual-fallback') return
    if (!slug || slug.length < 3) {
      setSlugAvailable(null)
      return
    }
    if (initialData?.slug && slug === initialData.slug) {
      setSlugAvailable(true)
      return
    }

    const timer = setTimeout(async () => {
      setCheckingSlug(true)
      try {
        // Exclude the user's own org so editing their slug doesn't collide
        // with themselves (they already own the current slug in the DB).
        const response = await api.post('/api/organizations/check-slug', {
          slug,
          ...(orgId ? { excludeOrgId: orgId } : {}),
        })
        setSlugAvailable(response.data.available)
      } catch {
        setSlugAvailable(null)
      } finally {
        setCheckingSlug(false)
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [slug, phase, orgId])

  // Cleanup polling and message cycling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
      if (scanMsgRef.current) clearInterval(scanMsgRef.current)
    }
  }, [])

  // ---- Phase A: URL Input ----

  const validateUrl = (): boolean => {
    const newErrors: Record<string, string> = {}
    const trimmed = websiteUrl.trim()
    if (!trimmed) {
      newErrors.website = 'Please enter your website URL'
    } else if (
      !/^(https?:\/\/)?[a-zA-Z0-9][-a-zA-Z0-9]*(\.[a-zA-Z0-9][-a-zA-Z0-9]*)+/.test(
        trimmed
      )
    ) {
      newErrors.website =
        'Please enter a valid website URL (e.g., www.yourfirm.com)'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleAnalyze = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateUrl()) return

    setIsSubmitting(true)
    try {
      let normalizedWebsite = websiteUrl.trim()
      if (!/^https?:\/\//i.test(normalizedWebsite)) {
        normalizedWebsite = `https://${normalizedWebsite}`
      }

      // Pull stashed access code (set by /redeem). Backend uses this to
      // create the org as a demo (no card, demo_expires_at) instead of a
      // 14-day trial. localStorage (not sessionStorage) so the code
      // survives the email-verification flow — verification typically
      // opens a new tab, which would have empty sessionStorage. Cleared
      // by OnboardingPage after the API consumes it.
      const accessCode = localStorage.getItem('app_access_code') || undefined
      const affiliateMeta = (() => {
        try {
          const raw = localStorage.getItem('legallady_affiliate_ref_meta')
          return raw ? JSON.parse(raw) : null
        } catch {
          return null
        }
      })()
      const affiliateExpired = affiliateMeta?.expiresAt && new Date(affiliateMeta.expiresAt).getTime() < Date.now()
      const affiliateCode = affiliateExpired
        ? undefined
        : localStorage.getItem('legallady_affiliate_ref') || undefined

      // Send only the website URL — backend auto-generates placeholder name + slug
      const response = await api.put('/api/onboarding/organization', {
        website: normalizedWebsite,
        timezone: detectBrowserTimezone(),
        ...(accessCode ? { accessCode } : {}),
        ...(affiliateCode ? { affiliateCode } : {}),
      })

      if (response.data.success) {
        if (response.data.token) {
          localStorage.setItem('app_auth_token', response.data.token)
          api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`
        }

        const id =
          response.data.organization?.id || response.data.organizationId
        if (!id) {
          toast({
            title: 'Error',
            description: 'Something went wrong. Please try again.',
            variant: 'destructive',
          })
          return
        }

        setOrgId(id)
        setPhase('scanning')
        startPolling()
        startMessageCycle()
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description:
          error.response?.data?.message || 'Failed to start analysis',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  // ---- Phase B: Scanning ----

  const startMessageCycle = () => {
    setScanMessageIndex(0)
    scanMsgRef.current = setInterval(() => {
      setScanMessageIndex((prev) =>
        prev < SCANNING_MESSAGES.length - 1 ? prev + 1 : prev
      )
    }, 4000)
  }

  // Wall-clock deadlines — fixed-tick budgets used to bail at 60s, which is
  // too short when Firecrawl fails and the backend falls back to axios/cheerio
  // (firecrawl retries internally for ~90s before giving up).
  // Foreground: shown to the user during the scanning animation.
  // Background: keeps trying after we've already dropped to manual-fallback,
  // so late results can backfill any blanks the user hasn't filled in yet.
  const POLL_INTERVAL_MS = 3000
  const FOREGROUND_DEADLINE_MS = 4 * 60_000
  const BACKGROUND_DEADLINE_MS = 3 * 60_000

  const startPolling = (mode: 'foreground' | 'background' = 'foreground') => {
    const deadline =
      Date.now() +
      (mode === 'foreground' ? FOREGROUND_DEADLINE_MS : BACKGROUND_DEADLINE_MS)

    const poll = async () => {
      if (Date.now() >= deadline) {
        stopPolling()
        if (mode === 'foreground') handleScrapeTimeout()
        return
      }
      try {
        const response = await api.get<ScrapedDataResponse>(
          '/api/onboarding/scraped-data'
        )
        if (
          response.data.success &&
          response.data.status === 'complete' &&
          response.data.data
        ) {
          stopPolling()
          if (mode === 'foreground') {
            handleScrapeComplete(response.data.data)
          } else {
            handleLateScrapeResult(response.data.data)
          }
        }
      } catch {
        // transient — keep polling until the deadline
      }
    }

    poll()
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS)
  }

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    if (scanMsgRef.current) {
      clearInterval(scanMsgRef.current)
      scanMsgRef.current = null
    }
  }

  const handleScrapeComplete = (data: ScrapedDataResponse['data']) => {
    if (!data) return

    if (data.businessName) setFirmName(data.businessName)
    if (data.practiceAreas?.length > 0) {
      const matched = data.practiceAreas.filter((area) =>
        PRACTICE_AREAS.some((pa) => pa.toLowerCase() === area.toLowerCase())
      )
      if (matched.length > 0) setPracticeAreas(matched)
    }
    if (data.description) setDescription(data.description)
    // Signup email wins over scraped email — the verified account owner email
    // is more reliable than whatever the scraper found on the site footer.
    const mergedContact = {
      ...(data.contactInfo || {}),
      email: signupEmail || data.contactInfo?.email || '',
    }
    setContactInfo(mergedContact)
    if (data.homepageScreenshot) setScreenshot(data.homepageScreenshot)

    setPhase('results')
  }

  const handleScrapeTimeout = () => {
    setPhase('manual-fallback')
    // Keep polling silently — backend may still be running through the
    // Firecrawl→axios fallback path. Late results backfill blank fields only,
    // so we never clobber anything the user has already typed.
    startPolling('background')
  }

  // Late completion handler used when results arrive after we've already
  // shown the manual-fallback form. Uses functional setters so we read the
  // latest field values at update time — that way we leave alone anything
  // the user has typed since the timeout fired.
  const handleLateScrapeResult = (data: ScrapedDataResponse['data']) => {
    if (!data) return

    setFirmName((prev) => prev || data.businessName || '')
    setDescription((prev) => prev || data.description || '')
    setPracticeAreas((prev) => {
      if (prev.length > 0) return prev
      const matched = (data.practiceAreas || []).filter((area) =>
        PRACTICE_AREAS.some((pa) => pa.toLowerCase() === area.toLowerCase())
      )
      return matched.length > 0 ? matched : prev
    })
    setContactInfo((prev) => ({
      phone: prev.phone || data.contactInfo?.phone || '',
      email: prev.email || data.contactInfo?.email || signupEmail || '',
      address: prev.address || data.contactInfo?.address || '',
    }))
    setScreenshot((prev) => prev || data.homepageScreenshot || null)

    toast({
      title: 'Site analysis complete',
      description: 'We finished analyzing your site — filled in any blanks we found.',
    })
  }

  const handleSkipToManual = () => {
    stopPolling()
    setPhase('manual-fallback')
  }

  // ---- Phase C: Results Confirm ----

  const handleConfirm = async () => {
    if (!firmName.trim()) {
      toast({
        title: 'Firm name required',
        description: 'Please enter your firm or business name.',
        variant: 'destructive',
      })
      return
    }

    setIsConfirming(true)
    try {
      // Persist the firm name/slug plus any edits the user made to scraped
      // fields (description, contact info, practice areas). The settings
      // fields land in organizations.settings via jsonb merge.
      await api.put(`/api/organizations/${orgId}`, {
        name: firmName.trim(),
        slug: slug,
        settings: {
          // scrapeConfirmed gates step 2 completion server-side — without it,
          // refreshing mid-scrape would auto-advance past the verify screen
          // because the org row exists before the user has seen the results.
          scrapeConfirmed: true,
          detectedBusinessName: firmName.trim(),
          businessDescription: description,
          detectedPracticeAreas: practiceAreas,
          detectedContactInfo: {
            phone: contactInfo.phone || '',
            email: contactInfo.email || '',
            address: contactInfo.address || '',
          },
        },
      })

      onComplete({
        id: orgId,
        name: firmName.trim(),
        slug,
        website: websiteUrl,
        practiceAreas,
        description,
        organizationName: firmName.trim(),
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description:
          error.response?.data?.message || 'Failed to save. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsConfirming(false)
    }
  }

  // ---- Manual Fallback Confirm ----

  const handleManualConfirm = async (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: Record<string, string> = {}
    if (!firmName.trim()) newErrors.name = 'Firm name is required'
    if (!slug.trim() || slug.length < 3) newErrors.slug = 'Dashboard URL is required (min 3 characters)'
    if (slugAvailable === false) newErrors.slug = 'This URL is already taken'
    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) return

    setIsConfirming(true)
    try {
      await api.put(`/api/organizations/${orgId}`, {
        name: firmName.trim(),
        slug,
        settings: { timezone: manualTimezone },
      })

      onComplete({
        id: orgId,
        name: firmName.trim(),
        slug,
        website: websiteUrl,
        practiceAreas,
        description,
        organizationName: firmName.trim(),
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description:
          error.response?.data?.message || 'Failed to save. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setIsConfirming(false)
    }
  }

  // ---- Practice area toggle ----

  const togglePracticeArea = (area: string) => {
    setPracticeAreas((prev) =>
      prev.includes(area) ? prev.filter((a) => a !== area) : [...prev, area]
    )
  }

  // ---- Render ----

  return (
    <div className="w-full max-w-2xl lg:max-w-none mx-auto">
      <AnimatePresence mode="wait">
        {/* ===== PHASE A: URL INPUT ===== */}
        {phase === 'input' && (
          <motion.div
            key="input"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center lg:text-left mb-8 max-w-xl lg:mx-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand-periwinkle/80 mb-3">
                Step 02 &mdash; Your firm
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-[2rem] font-heading font-semibold text-white tracking-tight leading-[1.1]">
                Point us at your website.
              </h2>
              <p className="text-white/60 mt-3 text-[15px] leading-relaxed max-w-md mx-auto lg:mx-0">
                We'll read it, learn your firm, and set up your AI agent's
                knowledge base &mdash; practice areas, contact info, the works.
              </p>
            </div>

            <form onSubmit={handleAnalyze} className="max-w-xl mx-auto lg:mx-0">
              <div className="space-y-2 mb-6">
                <Label htmlFor="website" className="sr-only">
                  Website URL
                </Label>
                <div className="relative">
                  <Globe className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/40" strokeWidth={1.75} />
                  <Input
                    id="website"
                    type="text"
                    inputMode="url"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder="www.yourfirm.com"
                    value={websiteUrl}
                    onChange={(e) => {
                      setWebsiteUrl(e.target.value)
                      if (errors.website) setErrors({})
                    }}
                    className={`pl-12 pr-4 h-14 rounded-xl text-base !bg-white/[0.04] !border-white/10 focus:!border-brand-royal/60 transition-[border-color,box-shadow,background-color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] ${
                      errors.website ? '!border-destructive' : ''
                    }`}
                    autoFocus
                    disabled={isSubmitting}
                  />
                </div>
                {errors.website && (
                  <p className="text-xs text-destructive">{errors.website}</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || !websiteUrl.trim()}
                className="w-full h-12 rounded-xl text-[15px] bg-gradient-to-r from-brand-royal to-brand-periwinkle hover:brightness-110 text-white font-semibold shadow-lg shadow-brand-royal/30 hover:shadow-brand-royal/50 transition-[transform,box-shadow,filter] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] disabled:active:scale-100 border-0"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Starting analysis…
                  </>
                ) : (
                  'Analyze my website'
                )}
              </Button>

              <p className="text-[11px] text-white/35 text-center lg:text-left mt-2.5">
                Public pages only. Takes about 20 seconds.
              </p>

              {onBack && (
                <div className="mt-4 text-center lg:text-left">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={onBack}
                    className="!text-white/50 hover:!text-white hover:!bg-white/5 text-sm transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.98]"
                  >
                    Go back
                  </Button>
                </div>
              )}
            </form>
          </motion.div>
        )}

        {/* ===== PHASE B: SCANNING ANIMATION ===== */}
        {phase === 'scanning' && (
          <motion.div
            key="scanning"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="text-center py-8"
          >
            {/* URL badge */}
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-1.5 text-xs text-white/60 mb-8">
              <Link2 className="h-3 w-3" />
              {websiteUrl.replace(/^https?:\/\//i, '').replace(/\/$/, '')}
            </div>

            {/* Pulsing orb */}
            <div className="flex justify-center mb-8">
              <motion.div
                className="relative h-24 w-24 rounded-full bg-gradient-to-br from-brand-royal/30 to-brand-periwinkle/30 flex items-center justify-center"
                animate={{
                  scale: [1, 1.15, 1],
                  opacity: [0.7, 1, 0.7],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <motion.div
                  className="absolute inset-2 rounded-full bg-gradient-to-br from-brand-royal/40 to-brand-periwinkle/40"
                  animate={{
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: 0.3,
                  }}
                />
                <Sparkles className="h-8 w-8 text-brand-periwinkle relative z-10" />
              </motion.div>
            </div>

            {/* Cycling status messages */}
            <div className="h-12 flex items-center justify-center">
              <AnimatePresence mode="wait">
                <motion.p
                  key={scanMessageIndex}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.4 }}
                  className="text-white/80 text-base font-medium"
                >
                  {SCANNING_MESSAGES[scanMessageIndex]}
                </motion.p>
              </AnimatePresence>
            </div>

            {/* Subtle progress shimmer */}
            <div className="mt-8 mx-auto max-w-xs">
              <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-brand-royal to-brand-periwinkle rounded-full"
                  animate={{ x: ['-100%', '100%'] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                  style={{ width: '40%' }}
                />
              </div>
            </div>

            {/* Skip link */}
            <button
              type="button"
              onClick={handleSkipToManual}
              className="mt-8 text-xs text-white/40 hover:text-white/60 transition-colors underline underline-offset-2"
            >
              Skip — I'll enter details manually
            </button>
          </motion.div>
        )}

        {/* ===== PHASE C: RESULTS REVEAL ===== */}
        {phase === 'results' && (
          <motion.div
            key="results"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* ==================================================================
                HERO — gallery-style, confident, breathable
                ================================================================== */}
            <motion.div
              className="text-center lg:text-left mb-12 lg:mb-14"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-300/90 mb-3 flex items-center gap-2 justify-center lg:justify-start">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Analysis complete
              </p>
              <h2 className="text-[2.25rem] sm:text-5xl lg:text-[3.25rem] font-heading font-semibold text-white tracking-tight leading-[1.02]">
                Here's what<br className="hidden sm:inline" /> we found.
              </h2>
              {firmName.trim() && (
                <p className="mt-4 text-[15px] sm:text-base text-white/55 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                  <span className="text-white/85 font-medium">{firmName.trim()}</span>
                  {' '}&middot; Review each section, tweak anything, then confirm.
                </p>
              )}
              {!firmName.trim() && (
                <p className="mt-4 text-[15px] text-white/55 max-w-md mx-auto lg:mx-0">
                  Review each section, edit anything, then confirm.
                </p>
              )}
            </motion.div>

            <div className="lg:grid lg:grid-cols-[1fr_320px] xl:grid-cols-[1fr_360px] lg:gap-10">
            <motion.div
              className="space-y-12 min-w-0"
              initial="hidden"
              animate="show"
              variants={{
                hidden: {},
                show: { transition: { staggerChildren: 0.1, delayChildren: 0.2 } },
              }}
            >
              {/* ============================================================
                  01 — FIRM IDENTITY
                  ============================================================ */}
              <motion.section
                variants={{
                  hidden: { opacity: 0, y: 16 },
                  show: { opacity: 1, y: 0, transition: { ease: [0.23, 1, 0.32, 1], duration: 0.5 } },
                }}
                className="border-t border-white/8 pt-6"
              >
                <div className="flex items-baseline justify-between mb-4 gap-3">
                  <div className="flex items-baseline gap-3">
                    <span className="font-mono text-[11px] text-white/30 tabular-nums">01</span>
                    <h3 className="text-sm font-semibold text-white/90 tracking-tight">Firm identity</h3>
                  </div>
                  {slug && !editingSlug && (
                    <button
                      type="button"
                      onClick={() => setEditingSlug(true)}
                      className="inline-flex items-center gap-1 text-[11px] text-brand-periwinkle hover:text-white transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]"
                    >
                      <Pencil className="h-2.5 w-2.5" strokeWidth={2} />
                      Edit URL
                    </button>
                  )}
                </div>
                <Input
                  id="firmName"
                  value={firmName}
                  onChange={(e) => setFirmName(e.target.value)}
                  placeholder="Your law firm's name"
                  className="!h-12 !rounded-xl !bg-white/[0.04] !border-white/10 text-white text-[15px] focus:!border-brand-royal/60 transition-[border-color,box-shadow,background-color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]"
                />
                {slug && !editingSlug && (
                  <p className="text-xs text-white/45 mt-2.5">
                    Workspace identifier:{' '}
                    <span className="text-white/75 font-mono">{slug}</span>{' '}
                    · Sign in at <span className="text-white/75 font-mono">app.legallady.ai</span>
                  </p>
                )}
                {editingSlug && (
                  <div className="mt-3 space-y-1.5">
                    <div className="relative">
                      <Input
                        value={slug}
                        onChange={(e) =>
                          setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
                        }
                        placeholder="your-firm-name"
                        className="!h-11 !rounded-xl !bg-white/[0.04] !border-white/10 text-white pr-9 text-sm font-mono"
                      />
                      {checkingSlug && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-white/40" />
                      )}
                      {!checkingSlug && slugAvailable === true && slug.length >= 3 && (
                        <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-emerald-400" strokeWidth={2.5} />
                      )}
                      {!checkingSlug && slugAvailable === false && (
                        <X className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-red-400" strokeWidth={2.5} />
                      )}
                    </div>
                    {slugAvailable === false && (
                      <p className="text-xs text-red-400">This URL is already taken</p>
                    )}
                    <button
                      type="button"
                      onClick={() => setEditingSlug(false)}
                      className="text-xs text-white/50 hover:text-white/75 transition-colors"
                    >
                      Done
                    </button>
                  </div>
                )}
              </motion.section>

              {/* ============================================================
                  02 — PRACTICE AREAS
                  ============================================================ */}
              <motion.section
                variants={{
                  hidden: { opacity: 0, y: 16 },
                  show: { opacity: 1, y: 0, transition: { ease: [0.23, 1, 0.32, 1], duration: 0.5 } },
                }}
                className="border-t border-white/8 pt-6"
              >
                <div className="flex items-baseline justify-between mb-4 gap-3">
                  <div className="flex items-baseline gap-3">
                    <span className="font-mono text-[11px] text-white/30 tabular-nums">02</span>
                    <h3 className="text-sm font-semibold text-white/90 tracking-tight">Practice areas</h3>
                  </div>
                  <p className="text-[11px] text-white/40">
                    {practiceAreas.length > 0
                      ? `${practiceAreas.length} selected`
                      : 'Pick yours below'}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {PRACTICE_AREAS.map((area) => {
                    const selected = practiceAreas.includes(area)
                    return (
                      <button
                        key={area}
                        type="button"
                        onClick={() => togglePracticeArea(area)}
                        className={`px-3.5 py-2 rounded-full text-[13px] font-medium transition-[background-color,color,border-color,transform,box-shadow] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-95 ${
                          selected
                            ? 'bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white border border-brand-royal/60 shadow-md shadow-brand-royal/25'
                            : 'bg-white/[0.04] text-white/75 border border-white/10 hover:bg-white/[0.08] hover:border-white/20 hover:text-white'
                        }`}
                      >
                        {selected && <Check className="inline h-3 w-3 mr-1 -mt-0.5" strokeWidth={2.5} />}
                        {area}
                      </button>
                    )
                  })}
                </div>
              </motion.section>

              {/* ============================================================
                  03 — WHAT THE FIRM DOES
                  ============================================================ */}
              <motion.section
                variants={{
                  hidden: { opacity: 0, y: 16 },
                  show: { opacity: 1, y: 0, transition: { ease: [0.23, 1, 0.32, 1], duration: 0.5 } },
                }}
                className="border-t border-white/8 pt-6"
              >
                <div className="flex items-baseline justify-between mb-4 gap-3">
                  <div className="flex items-baseline gap-3">
                    <span className="font-mono text-[11px] text-white/30 tabular-nums">03</span>
                    <h3 className="text-sm font-semibold text-white/90 tracking-tight">About the firm</h3>
                  </div>
                  <p className="text-[11px] text-white/40">Feeds your AI</p>
                </div>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Briefly describe what your firm does and who you serve…"
                  rows={4}
                  className="!rounded-xl !bg-white/[0.04] !border-white/10 text-white text-[15px] leading-relaxed focus:!border-brand-royal/60 transition-[border-color,box-shadow,background-color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]"
                />
                <p className="text-xs text-white/40 mt-2">
                  This is what your AI reads to give accurate, firm-specific answers to callers and visitors.
                </p>
              </motion.section>

              {/* ============================================================
                  04 — CONTACT INFO
                  ============================================================ */}
              <motion.section
                variants={{
                  hidden: { opacity: 0, y: 16 },
                  show: { opacity: 1, y: 0, transition: { ease: [0.23, 1, 0.32, 1], duration: 0.5 } },
                }}
                className="border-t border-white/8 pt-6"
              >
                <div className="flex items-baseline justify-between mb-4 gap-3">
                  <div className="flex items-baseline gap-3">
                    <span className="font-mono text-[11px] text-white/30 tabular-nums">04</span>
                    <h3 className="text-sm font-semibold text-white/90 tracking-tight">Contact</h3>
                  </div>
                  <p className="text-[11px] text-white/40">Edit anything that's wrong</p>
                </div>
                <div className="space-y-3">
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" strokeWidth={1.75} />
                    <Input
                      id="contact-phone"
                      type="tel"
                      inputMode="tel"
                      value={contactInfo.phone || ''}
                      onChange={(e) => setContactInfo({ ...contactInfo, phone: e.target.value })}
                      placeholder="(310) 447-8675"
                      className="!h-12 !rounded-xl pl-11 !bg-white/[0.04] !border-white/10 text-white text-[15px] focus:!border-brand-royal/60 transition-[border-color,box-shadow,background-color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]"
                    />
                  </div>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" strokeWidth={1.75} />
                    <Input
                      id="contact-email"
                      type="email"
                      inputMode="email"
                      autoCapitalize="none"
                      value={contactInfo.email || ''}
                      onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
                      placeholder="contact@yourfirm.com"
                      className="!h-12 !rounded-xl pl-11 !bg-white/[0.04] !border-white/10 text-white text-[15px] focus:!border-brand-royal/60 transition-[border-color,box-shadow,background-color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]"
                    />
                  </div>
                  <div className="relative">
                    <MapPin className="absolute left-4 top-3.5 h-4 w-4 text-white/40" strokeWidth={1.75} />
                    <Textarea
                      id="contact-address"
                      value={contactInfo.address || ''}
                      onChange={(e) => setContactInfo({ ...contactInfo, address: e.target.value })}
                      placeholder="Street, City, State ZIP"
                      rows={2}
                      className="!rounded-xl pl-11 !bg-white/[0.04] !border-white/10 text-white text-[15px] focus:!border-brand-royal/60 transition-[border-color,box-shadow,background-color] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)]"
                    />
                  </div>
                </div>
              </motion.section>

              {/* ============================================================
                  ACTIONS
                  ============================================================ */}
              <motion.div
                variants={{
                  hidden: { opacity: 0, y: 16 },
                  show: { opacity: 1, y: 0, transition: { ease: [0.23, 1, 0.32, 1], duration: 0.5 } },
                }}
                className="flex items-center gap-3 pt-2 border-t border-white/8"
                style={{ paddingTop: '1.5rem' }}
              >
                <Button
                  onClick={handleConfirm}
                  disabled={isConfirming || !firmName.trim() || slugAvailable === false || checkingSlug}
                  className="h-12 px-6 rounded-xl text-[15px] bg-gradient-to-r from-brand-royal to-brand-periwinkle hover:brightness-110 text-white font-semibold shadow-lg shadow-brand-royal/30 hover:shadow-brand-royal/50 transition-[transform,box-shadow,filter] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] disabled:active:scale-100 border-0 inline-flex items-center gap-2"
                >
                  {isConfirming ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>
                      Looks good &mdash; continue
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
                    </>
                  )}
                </Button>
                {onBack && (
                  <Button
                    variant="ghost"
                    onClick={onBack}
                    className="!text-white/55 hover:!text-white hover:!bg-white/5 text-sm font-medium h-12 px-5 transition-colors duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.98]"
                  >
                    Back
                  </Button>
                )}
              </motion.div>

            </motion.div>

            {/* Right column — screenshot preview. Desktop-only (hidden on
                mobile) because a broken/empty scrape screenshot eats a huge
                amount of scroll on phones for no informational value. */}
            <motion.div
              className="hidden lg:block space-y-3 min-w-0 lg:sticky lg:top-6 lg:self-start"
              initial="hidden"
              animate="show"
              variants={{
                hidden: {},
                show: { transition: { staggerChildren: 0.15, delayChildren: 0.3 } },
              }}
            >
              {screenshot && screenshotStatus !== 'failed' && (
                <motion.div
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    show: { opacity: 1, y: 0 },
                  }}
                >
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/40 mb-3">
                    Your website
                  </p>
                  {screenshotStatus === 'blank' ? (
                    <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-brand-royal/15 via-brand-royal/8 to-brand-periwinkle/10 border border-white/10 aspect-[16/10] flex flex-col items-center justify-center p-5 text-center">
                      <div className="h-10 w-10 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center mb-2.5">
                        <Globe className="h-5 w-5 text-brand-periwinkle" strokeWidth={1.75} />
                      </div>
                      <p className="text-xs font-medium text-white/75 leading-tight">
                        Preview unavailable
                      </p>
                      {websiteUrl && (
                        <p className="text-[11px] text-brand-periwinkle/80 mt-1 break-all px-2 font-mono">
                          {websiteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                        </p>
                      )}
                      <p className="text-[10px] text-white/40 mt-1.5 leading-snug px-2">
                        We captured your site's content &mdash; the homepage image just didn't render in time.
                      </p>
                    </div>
                  ) : (
                    <div className="rounded-2xl overflow-hidden bg-white/5 border border-white/10 shadow-xl shadow-black/40 aspect-[16/10]">
                      <img
                        src={
                          // Firecrawl v1 returns a URL; older responses may return raw base64.
                          screenshot.startsWith('data:') || /^https?:\/\//i.test(screenshot)
                            ? screenshot
                            : `data:image/png;base64,${screenshot}`
                        }
                        alt="Homepage screenshot"
                        crossOrigin={screenshot.startsWith('http') ? 'anonymous' : undefined}
                        onLoad={(e) => checkImageBlank(e.currentTarget)}
                        onError={() => setScreenshotStatus('failed')}
                        className="w-full h-full object-cover object-top"
                        style={{ opacity: screenshotStatus === 'loading' ? 0 : 1, transition: 'opacity 200ms' }}
                      />
                    </div>
                  )}
                </motion.div>
              )}
            </motion.div>
            </div>
          </motion.div>
        )}

        {/* ===== MANUAL FALLBACK ===== */}
        {phase === 'manual-fallback' && (
          <motion.div
            key="manual"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
          >
            <div className="text-center mb-6">
              <h2 className="text-xl sm:text-2xl font-heading font-semibold text-white tracking-tight">
                Tell us about your firm
              </h2>
              <Card className="mt-3 !bg-amber-500/10 !border-amber-400/20">
                <CardContent className="pt-3 pb-3">
                  <div className="flex items-center gap-2 text-amber-200 text-sm">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                    <span>
                      We couldn't fully analyze your website. No worries — just
                      fill in the details below.
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>

            <form onSubmit={handleManualConfirm} className="space-y-4 max-w-md mx-auto">
              <fieldset disabled={isConfirming} className="space-y-4 border-0 p-0 m-0 min-w-0">
                <div className="space-y-2">
                  <Label htmlFor="manual-name">Firm Name</Label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="manual-name"
                      value={firmName}
                      onChange={(e) => setFirmName(e.target.value)}
                      placeholder="Your Law Firm Name"
                      className={`pl-10 ${errors.name ? 'border-destructive' : ''}`}
                    />
                  </div>
                  {errors.name && (
                    <p className="text-xs text-destructive">{errors.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="manual-slug">Dashboard URL</Label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="manual-slug"
                      value={slug}
                      onChange={(e) => {
                        setEditingSlug(true)
                        setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
                      }}
                      placeholder="your-firm-name"
                      className={`pl-10 ${errors.slug ? 'border-destructive' : slugAvailable === true ? 'border-green-500' : ''}`}
                    />
                    {checkingSlug && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
                    {!checkingSlug && slugAvailable === true && slug.length >= 3 && <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />}
                    {!checkingSlug && slugAvailable === false && <X className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-destructive" />}
                  </div>
                  {errors.slug && (
                    <p className="text-xs text-destructive">{errors.slug}</p>
                  )}
                  {!errors.slug && slug && (
                    <p className="text-xs text-muted-foreground break-all">
                      Workspace: <span className="text-white/80 font-mono">{slug}</span>
                      {' · '}Sign in at <span className="text-white/80 font-mono">app.legallady.ai</span>
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="manual-tz">Time zone</Label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10 pointer-events-none" />
                    <Select value={manualTimezone} onValueChange={setManualTimezone}>
                      <SelectTrigger id="manual-tz" className="pl-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {US_TIMEZONES.map((tz) => (
                          <SelectItem key={tz.value} value={tz.value}>
                            {tz.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Practice Areas</Label>
                  <div className="flex flex-wrap gap-2">
                    {PRACTICE_AREAS.map((area) => {
                      const selected = practiceAreas.includes(area)
                      return (
                        <button
                          key={area}
                          type="button"
                          onClick={() => togglePracticeArea(area)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-[background-color,color,border-color,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-95 ${
                            selected
                              ? 'bg-brand-royal text-white border border-brand-royal shadow-sm'
                              : 'bg-white/10 text-white/70 border border-white/15 hover:bg-white/15'
                          }`}
                        >
                          {selected && <Check className="inline h-3 w-3 mr-1 -mt-0.5" />}
                          {area}
                        </button>
                      )
                    })}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="manual-desc">Business Description (optional)</Label>
                  <Textarea
                    id="manual-desc"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Briefly describe your business..."
                    rows={3}
                  />
                </div>
              </fieldset>

              <div className="flex gap-3">
                {onBack && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={onBack}
                    className="!text-white/70 hover:!text-white hover:!bg-white/10"
                  >
                    Back
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={isConfirming || slugAvailable === false || checkingSlug}
                  className="flex-1 h-11 bg-gradient-to-r from-brand-royal to-brand-periwinkle hover:brightness-110 text-white font-semibold shadow-lg shadow-brand-royal/30 hover:shadow-brand-royal/50 transition-[transform,box-shadow,filter] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] active:scale-[0.97] disabled:active:scale-100 border-0"
                >
                  {isConfirming ? 'Saving...' : 'Continue'}
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
